// The Translation Scan — deterministic client-side analysis of a visitor's own
// copy. No LLM: every output is computed from the input text so it is real,
// specific, and instant. This is the spine of the F1 diagnostic instrument.
//
// Architected so the same ScanResult shape can later be produced by a real
// Claude edge function without touching the UI.

import { diagnostic, gaps, type Gap } from "./content"

export type GapScore = { gap: Gap; hits: number }

export type ScanResult = {
  wordCount: number
  sentenceCount: number
  avgSentenceLength: number
  clarityScore: number // 12–96, never absolute (credibility)
  buzzwords: string[] // found verbatim, in input order
  buzzwordDensity: number // per 100 words
  hasNumbers: boolean
  gap: Gap
  gapScores: GapScore[]
  tension: string
  before: string
  after: string
  verdict: string
}

const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "but", "of", "to", "in", "on", "for", "with",
  "we", "our", "us", "is", "are", "be", "that", "this", "it", "as", "at", "by",
  "from", "your", "you", "their", "they", "has", "have", "will", "can", "all",
])

// Domain nouns we know how to fold back into a rewrite, longest-first so
// "industrial storage" wins over "storage".
const DOMAIN_NOUNS = [
  "edge ai", "industrial storage", "test and measurement", "semiconductor",
  "instrumentation", "automation", "manufacturing", "storage", "memory",
  "chipset", "chip", "silicon", "sensors", "robotics", "pumps", "pump",
  "testing", "measurement", "platform", "software", "hardware", "battery",
  "materials", "pharma", "biotech", "energy", "wafer", "module", "system",
]

const PLACES = ["taiwan", "taipei", "asia", "japan", "korea", "china", "germany"]

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function countOccurrences(haystackLower: string, needle: string): boolean {
  // Phrase-aware, boundary-aware membership test.
  const pattern = new RegExp(`(?:^|[^a-z])${escapeRegExp(needle)}(?:[^a-z]|$)`, "i")
  return pattern.test(haystackLower)
}

export function analyzeCopy(raw: string): ScanResult | null {
  const text = raw.trim()
  const words = text.split(/\s+/).filter(Boolean)
  if (words.length < 4) return null

  const lower = ` ${text.toLowerCase()} `

  // 1. Buzzwords found verbatim, deduped, in order of first appearance.
  const seen = new Set<string>()
  const buzzwords: string[] = []
  for (const w of diagnostic.buzzwords) {
    if (countOccurrences(lower, w) && !seen.has(w)) {
      seen.add(w)
      buzzwords.push(w)
    }
  }
  const buzzwordDensity = (buzzwords.length / words.length) * 100

  // 2. Sentence shape.
  const sentences = text
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter((s) => s.split(/\s+/).filter(Boolean).length > 0)
  const sentenceCount = Math.max(sentences.length, 1)
  const avgSentenceLength = words.length / sentenceCount

  // 3. Specificity proxy: presence of any concrete number.
  const hasNumbers = /\d/.test(text)

  // 4. Gap detection by signal-keyword hits.
  const gapScores: GapScore[] = gaps
    .map((gap) => {
      const signals = diagnostic.gapSignals[gap.id] ?? []
      const hits = signals.reduce(
        (n, kw) => n + (countOccurrences(lower, kw) ? 1 : 0),
        0,
      )
      return { gap, hits }
    })
    .sort((a, b) => b.hits - a.hits)

  // Deterministic fallback when nothing matches: perception↔reality is the
  // universal default for vague copy.
  const top = gapScores[0]
  const detectedGap =
    top.hits > 0
      ? top.gap
      : gaps.find((g) => g.id === "perception-reality") ?? gaps[0]

  // 5. Clarity score — deterministic penalties, clamped to a credible band.
  const buzzPenalty = Math.min(46, buzzwords.length * 8)
  const lengthPenalty = clamp((avgSentenceLength - 17) * 2.2, 0, 26)
  const specificityPenalty = hasNumbers ? 0 : 10
  const clarityScore = Math.round(
    clamp(100 - buzzPenalty - lengthPenalty - specificityPenalty, 12, 96),
  )

  // 6. Rewrite — fold real tokens back in to prove it read THEIR words.
  const noun = detectDomainNoun(lower) ?? frequentToken(words) ?? "what you build"
  const place = PLACES.find((p) => countOccurrences(lower, p))
  const years = detectYears(text)
  const before = sentences[0] ? truncate(sentences[0], 96) : truncate(text, 96)
  const after = rewriteFor(detectedGap.id, { noun, place, years })

  return {
    wordCount: words.length,
    sentenceCount,
    avgSentenceLength: Math.round(avgSentenceLength * 10) / 10,
    clarityScore,
    buzzwords,
    buzzwordDensity: Math.round(buzzwordDensity * 10) / 10,
    hasNumbers,
    gap: detectedGap,
    gapScores,
    tension: diagnostic.tensionTemplates[detectedGap.id],
    before,
    after,
    verdict: verdictFor(clarityScore),
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n))
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s
}

function detectDomainNoun(lower: string): string | null {
  for (const noun of DOMAIN_NOUNS) {
    if (countOccurrences(lower, noun)) return noun
  }
  return null
}

function detectYears(text: string): number | null {
  const span = text.match(/(\d{1,3})\s*(?:\+?\s*)?years/i)
  if (span) return parseInt(span[1], 10)
  const founded = text.match(/(?:since|founded|established)[^\d]{0,12}(19|20)\d{2}/i)
  if (founded) {
    const year = parseInt(founded[0].match(/(19|20)\d{2}/)![0], 10)
    return 2026 - year
  }
  return null
}

function frequentToken(words: string[]): string | null {
  const freq = new Map<string, number>()
  for (const w of words) {
    const t = w.toLowerCase().replace(/[^a-z]/g, "")
    if (t.length < 4 || STOPWORDS.has(t)) continue
    freq.set(t, (freq.get(t) ?? 0) + 1)
  }
  let best: string | null = null
  let bestN = 0
  for (const [t, n] of freq) {
    if (n > bestN) {
      best = t
      bestN = n
    }
  }
  return best
}

function rewriteFor(
  gapId: string,
  ctx: { noun: string; place?: string; years: number | null },
): string {
  const { noun, place, years } = ctx
  const Noun = capitalize(noun)
  switch (gapId) {
    case "data-story":
      return `The ${noun} is precise. Here is the decision it lets your customer make.`
    case "legacy-future":
      return years
        ? `${years} years of ${noun}, aimed squarely at what comes next.`
        : `Decades of ${noun} mastery, reframed as proof, not the past.`
    case "tech-humanity":
      return `${Noun} the market can finally feel, not just spec.`
    case "local-global":
      return place
        ? `Engineered in ${capitalize(place)}. Built to be trusted everywhere.`
        : `Quietly world-class at home. Now said clearly enough to travel.`
    case "perception-reality":
    default:
      return `What you actually are, said plainly enough that the market stops guessing.`
  }
}

function verdictFor(score: number): string {
  if (score >= 80) return "Clear. A few sharp cuts would make it unforgettable."
  if (score >= 60) return "Legible, but the market is doing the translating for you."
  if (score >= 40) return "The signal is buried. Real value, hidden under abstraction."
  return "The market cannot tell what you do, or why it should care."
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
